/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.21.0
 * @since 1.0.0
 */

import type { FC } from 'react';

import type { ILabelEditorTranslation } from '@modules/internationalization/types/corporate';

import type { IEditableLabel } from '../labelEditorTypes';
import { LabelEditorItem } from './LabelEditorItem';
import { LabelEditorListContainerStyled, LabelEditorSearchNotFound } from './styled/LabelEditorListStyled';

export interface ILabelEditorListProps {
    isLoading?: boolean;
    labels: IEditableLabel[];
    searchValue: string;
    switchLabel?: (label: IEditableLabel) => void;
    removeLabel?: (id: number) => void;
    translation: ILabelEditorTranslation;
}

export const LabelEditorList: FC<ILabelEditorListProps> = (
    {
        isLoading,
        labels,
        searchValue,
        switchLabel,
        removeLabel,
        translation,
    },
) => {

    const labelsList = labels.filter(label =>
        !searchValue || label.name.toLowerCase().includes(searchValue.toLowerCase()));

    /**
     * @description Handler on item checked and call onCheckedChange prop.
     * @param {string} name
     * @param {boolean} value
     * @param {number} id
     * @return {VoidFunction}
     */

    const label = labelsList.map(label => (
        <LabelEditorItem key={label.id}
            label={label}
            removeLabel={removeLabel}
            searchValue={searchValue}
            switchLabel={switchLabel}
            translation={translation}
        />
    ));

    const searchNotFound = () => {
        return isLoading ? null : (
            <LabelEditorSearchNotFound className="label-editor__search-not-found" role='none'>
                {translation.noSearchMatcher}
            </LabelEditorSearchNotFound>
    )};

    return (
        <LabelEditorListContainerStyled data-component="labelsList">
            {!!labelsList.length ? label : searchNotFound()}
        </LabelEditorListContainerStyled>
    );
};
